import type { ConfigObjectMatch, ContentUpdateResult } from '@cli-types/next-adapter/init.js'
import { findMatchingBrace } from '@cli-utils/js-source-scan.js'
import { detectPropertyIndent } from './detect-property-indent.js'
import { findConfigObject } from './find-config-object.js'
import { findTopLevelProperty } from './find-top-level-property.js'
import { removeTopLevelProperty } from './remove-top-level-property.js'

export function ensureExperimentalUseCache(
  content: string,
  configObject: ConfigObjectMatch
): ContentUpdateResult {
  let updatedContent = content
  let changed = false

  const withoutCacheComponents = removeTopLevelProperty(
    updatedContent,
    configObject,
    'cacheComponents'
  )
  updatedContent = withoutCacheComponents.content
  changed = withoutCacheComponents.changed

  const refreshedConfigObject = findConfigObject(updatedContent)
  if (!refreshedConfigObject) {
    return { content, changed: false, unsupported: true }
  }

  const experimentalProp = findTopLevelProperty(
    updatedContent,
    refreshedConfigObject.start,
    refreshedConfigObject.end,
    'experimental'
  )

  if (!experimentalProp) {
    return {
      content:
        updatedContent.slice(0, refreshedConfigObject.start + 1) +
        `\n${refreshedConfigObject.propertyIndent}experimental: {\n${refreshedConfigObject.propertyIndent}  useCache: true,\n${refreshedConfigObject.propertyIndent}},` +
        updatedContent.slice(refreshedConfigObject.start + 1),
      changed: true
    }
  }

  const experimentalValue = experimentalProp.value.trim()
  if (!experimentalValue.startsWith('{')) {
    return { content, changed: false, unsupported: true }
  }

  const experimentalObjectStart = updatedContent.indexOf('{', experimentalProp.valueStart)
  if (experimentalObjectStart < 0) {
    return { content, changed: false, unsupported: true }
  }

  const experimentalObjectEnd = findMatchingBrace(updatedContent, experimentalObjectStart)
  if (experimentalObjectEnd < 0) {
    return { content, changed: false, unsupported: true }
  }

  const useCacheProp = findTopLevelProperty(
    updatedContent,
    experimentalObjectStart,
    experimentalObjectEnd,
    'useCache'
  )

  if (useCacheProp) {
    if (useCacheProp.value.trim() === 'true') {
      return { content: updatedContent, changed }
    }

    return {
      content:
        updatedContent.slice(0, useCacheProp.valueStart) +
        'true' +
        updatedContent.slice(useCacheProp.valueEnd),
      changed: true
    }
  }

  const propertyIndent = detectPropertyIndent(updatedContent, experimentalObjectStart)
  return {
    content:
      updatedContent.slice(0, experimentalObjectStart + 1) +
      `\n${propertyIndent}useCache: true,` +
      updatedContent.slice(experimentalObjectStart + 1),
    changed: true
  }
}
